import {
  Box,
  Button,
  Container,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  SURVEY_CREATE_NAMED_STEPS,
  SURVEY_CREATE_STEPS,
} from '../SurveyCreateConfig';
import StepIconComp, { ColorlibConnector } from '../Components/StepIconComp';
import { getValidationSchema, getValidationSchemaNamed } from '../validation';
import { useNavigate, useParams } from 'react-router-dom';

import ConfigureSurveyPageStep from '../stepsPages/ConfigureSurveyPageStep';
import { DOCUMENT_UPLOAD_ENUM } from '../../../components/survey/surveyParams-config';
import LoaderAnimation from '../../../components/LoaderComp/LoaderAnimation';
import NamedSurveyStep from '../stepsPages/NamedSurveyStep';
import StartingScheduleStep from '../stepsPages/StartingScheduleStep';
import StepsCompletedPage from '../stepsPages/StepsCompletedPage';
import SurveyDetailsStep from '../stepsPages/SurveyDetailsStep';
import Toast from '../../../components/Tost';
import useApiService from '../../../hooks/api-services/use-service';
import { useFormik } from 'formik';
import { useTitle } from '../../../contexts/TitleContext';
import { useToast } from '../../../hooks/custom-hook-functions/useToast';

const SurveyEdit = () => {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const { setTitle } = useTitle();
  const [isLoading, setIsLoading] = useState(false);
  const { getSurveyById, putSurvey, surveyDocumentUpload } = useApiService();
  const { showToast, toast, setToast, toastMessage, toastType } = useToast();
  const [surveyData, setSurveyData] = useState(null);
  const decodedSurveyId = atob(surveyId).split('_')[1];

  const [fileUploading, setFileUploading] = useState(false);

  // IMAGE
  const [fileUploadData, setFileUploadData] = useState({
    document_type: DOCUMENT_UPLOAD_ENUM.SURVEY,
    file_name: null,
  });

  // STEPPER
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(SURVEY_CREATE_STEPS);

  useEffect(() => {
    if (decodedSurveyId) {
      getSurveysData(decodedSurveyId);
    }
  }, [decodedSurveyId]);

  const getSurveysData = async decodedSurveyId => {
    try {
      setIsLoading(true);
      const surveyDataResponse = await getSurveyById(decodedSurveyId);
      setSurveyData(surveyDataResponse.data);
      setTitle(surveyDataResponse?.data?.title);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      showToast('Something went wrong. Please try again!', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const surveyFormik = useFormik({
    initialValues: {
      title: surveyData?.title || '',
      description: surveyData?.description || '',
      is_anonymous:
        surveyData?.is_anonymous !== undefined
          ? surveyData.is_anonymous
          : false,
      status: surveyData?.status || 'draft',
      target_audience: surveyData?.target_audience || 'all',
      is_active:
        surveyData?.is_active !== undefined ? surveyData.is_active : true,
      survey_logo: '',
      survey_display_type: surveyData?.survey_display_type || '', // Single Form, Multi Steps
      multi_step_style: surveyData?.multi_step_style || '', // question_category, one_question_per_step
      is_respondent_details: '',
      banner_image: surveyData?.banner_image || '',
      is_respondent_name:
        surveyData?.is_respondent_name !== undefined
          ? surveyData.is_respondent_name
          : false,
      is_respondent_email:
        surveyData?.is_respondent_email !== undefined
          ? surveyData.is_respondent_email
          : false,
      is_respondent_phone:
        surveyData?.is_respondent_phone !== undefined
          ? surveyData.is_respondent_phone
          : false,
      is_respondent_gender:
        surveyData?.is_respondent_gender !== undefined
          ? surveyData.is_respondent_gender
          : false,
      startOption: surveyData?.start_date ? 'future' : 'immediately',
      start_date: surveyData?.start_date || '',
      end_date: surveyData?.end_date || '',
      banner_image_url: surveyData?.banner_image_url || '',
      is_score: surveyData?.is_score || false,
    },
    enableReinitialize: true,
    validate: values => {
      const schema = values.is_anonymous
        ? getValidationSchema(activeStep)
        : getValidationSchemaNamed(activeStep);
      return schema(values);
    },
    onSubmit: values => {
      handleEditSurvey(values);
    },
  });

  const handleNext = async () => {
    const errors = await surveyFormik.validateForm();
    if (Object.keys(errors).length === 0) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    } else {
      surveyFormik.setTouched(errors);
    }
  };

  const handleNextAndSubmit = async () => {
    if (activeStep === steps.length - 1) {
      surveyFormik.handleSubmit();
    } else {
      await handleNext();
    }
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleReset = () => {
    surveyFormik.resetForm();
  };

  const excludeFields = [
    'startOption',
    'is_respondent_details',
    'banner_image_url',
    'survey_logo',
  ];

  const handleFileUpload = async () => {
    try {
      setFileUploading(true);
      let fileUploadFormData = new FormData();
      if (fileUploadData.file_name) {
        Object.keys(fileUploadData).forEach(key => {
          fileUploadFormData.append(key, fileUploadData[key]);
        });
        const response = await surveyDocumentUpload(fileUploadFormData);
        setFileUploading(false);
        return response.data;
      }
    } catch (err) {
      setFileUploading(false);
      showToast('Error uploading your image file', 'error');
    } finally {
      setFileUploading(false);
    }
  };

  const handleEditSurvey = async values => {
    try {
      setIsLoading(true);
      let formData = new FormData();

      Object.keys(values).forEach(key => {
        if (!excludeFields.includes(key)) {
          formData.append(key, values[key]);
        }
      });

      if (fileUploadData.file_name) {
        const uploadResult = await handleFileUpload();
        if (uploadResult) {
          formData.append('banner_image', uploadResult.id);
        }
      }

      const response = await putSurvey(decodedSurveyId, formData);
      if (response.status === 200) {
        showToast('Survey updated successfully', 'success');
        setIsLoading(false);
        setTimeout(() => {
          window.history.back();
        }, 800);
      }
    } catch (error) {
      showToast('Error updating survey', 'error');
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const surveyTypeValue = surveyFormik.values.is_anonymous;
  useEffect(() => {
    if (surveyTypeValue === false) {
      setSteps(SURVEY_CREATE_NAMED_STEPS);
    } else {
      setSteps(SURVEY_CREATE_STEPS);
    }
  }, [surveyTypeValue]);

  const getActiveStepComponent = activeStep => {
    if (surveyTypeValue === false) {
      switch (activeStep) {
        case 0:
          return <SurveyDetailsStep formik={surveyFormik} editMode />;
        case 1:
          return <NamedSurveyStep formik={surveyFormik} editMode />;
        case 2:
          return (
            <ConfigureSurveyPageStep
              formik={surveyFormik}
              editMode
              fileUploadData={fileUploadData}
              setFileUploadData={setFileUploadData}
            />
          );
        case 3:
          return <StartingScheduleStep formik={surveyFormik} editMode />;
        default:
          return null;
      }
    } else {
      switch (activeStep) {
        case 0:
          return <SurveyDetailsStep formik={surveyFormik} editMode />;
        case 1:
          return (
            <ConfigureSurveyPageStep
              formik={surveyFormik}
              editMode
              fileUploadData={fileUploadData}
              setFileUploadData={setFileUploadData}
            />
          );
        case 2:
          return <StartingScheduleStep formik={surveyFormik} editMode />;
        default:
          return null;
      }
    }
  };

  const handleEditStep = index => {
    setActiveStep(index);
  };

  useEffect(() => {
    const handleBeforeUnload = event => {
      event.preventDefault();
      event.returnValue =
        'Are you sure you want to reload? All progress will be lost.';
    };

    const handleLoad = () => {
      const navigationEntry =
        window.performance.getEntriesByType('navigation')[0];
      if (navigationEntry && navigationEntry.type === 'reload') {
        navigate(`/survey-edit/${surveyId}`);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('load', handleLoad);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  if (isLoading) {
    return <LoaderAnimation />;
  }

  return (
    <Box sx={{ my: 'calc(100% - 98%)' }}>
      <Container
        maxWidth="lg"
        sx={{
          pt: 5,
          pb: 5,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          backgroundColor: 'white',
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '2rem',
            }}
          >
            <Stepper
              alternativeLabel
              activeStep={activeStep}
              connector={<ColorlibConnector />}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    StepIconComponent={props => {
                      const updatedPropsEditMode = {
                        ...props,
                        active: true,
                        completed: true,
                      };
                      return (
                        <StepIconComp
                          {...updatedPropsEditMode}
                          surveyType={surveyTypeValue}
                          editMode
                        />
                      );
                    }}
                    onClick={() => handleEditStep(index)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <div>
              {activeStep === steps.length ? (
                <StepsCompletedPage handleReset={handleReset} />
              ) : (
                <form onSubmit={surveyFormik.handleSubmit}>
                  {getActiveStepComponent(activeStep)}
                  <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                    <Button
                      color="inherit"
                      disabled={activeStep === 0 || isLoading || fileUploading}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                      variant="outlined"
                    >
                      Back
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button
                      onClick={handleNextAndSubmit}
                      disabled={isLoading || fileUploading}
                      variant="contained"
                      type={activeStep === steps.length ? 'submit' : 'button'}
                    >
                      {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                    </Button>
                  </Box>
                </form>
              )}
            </div>
          </Box>
        </Box>
      </Container>
      {toast && (
        <Toast
          setToast={setToast}
          isLoading={isLoading}
          toast={toast}
          toastType={toastType}
          toastMessage={toastMessage}
        />
      )}
    </Box>
  );
};

export default SurveyEdit;
